import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase.config';
import '../DeckRequestModal/DeckRequestModal.css';

export interface GovernanceDocSelection {
  documentKey: string;
  documentLabel: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  selection: GovernanceDocSelection | null;
}

const GovernanceRequestModal: React.FC<Props> = ({ open, onClose, selection }) => {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fields, setFields] = useState({ name: '', org: '', email: '', phone: '' });
  const modalCardRef = useRef<HTMLDivElement>(null);
  const previousActiveRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) {
      setSent(false);
      setError('');
      setFields({ name: '', org: '', email: '', phone: '' });
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    previousActiveRef.current = document.activeElement as HTMLElement | null;
    const firstFocusable = modalCardRef.current?.querySelector<HTMLElement>(
      'button[aria-label="Close"], input, .deck-modal-submit'
    );
    firstFocusable?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previousActiveRef.current?.focus?.();
    };
  }, [open, onClose]);

  const resetAndClose = () => {
    setError('');
    setSent(false);
    setFields({ name: '', org: '', email: '', phone: '' });
    onClose();
  };

  const handleClose = () => {
    if (sent) {
      resetAndClose();
      return;
    }
    onClose();
    setError('');
  };

  const submit = async () => {
    if (!selection) return;
    setError('');
    const name = fields.name.trim();
    const phone = fields.phone.trim();
    const email = fields.email.trim();
    if (!name || !phone || !email) {
      setError('Name, email and phone number are required.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    try {
      await addDoc(collection(db, 'governanceRequests'), {
        documentKey: selection.documentKey,
        documentLabel: selection.documentLabel,
        name,
        phone,
        organisation: fields.org.trim() || null,
        email,
        timestamp: serverTimestamp(),
        status: 'new',
      });
      setSent(true);
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again or contact us directly.');
    } finally {
      setLoading(false);
    }
  };

  if (!open || !selection) return null;

  return createPortal(
    <div
      className="deck-modal-overlay"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-label="Request governance document"
    >
      <div className="deck-modal-card" ref={modalCardRef} onClick={(e) => e.stopPropagation()}>
        <button className="deck-modal-close" onClick={handleClose} aria-label="Close" type="button">
          ×
        </button>
        <div className="deck-modal-inner">
          <div className="eyebrow" style={{ color: 'var(--blue)' }}>
            Request document access
          </div>
          <h2 className="deck-modal-title">
            {selection.documentLabel}
            <br />
            <span className="deck-modal-title-dim">Available to eligible investors.</span>
          </h2>
          <p className="deck-modal-desc">
            Share your details and our team will verify eligibility and follow up. This does not constitute an offer of
            securities.
          </p>
          {!sent ? (
            <>
              <div className="deck-modal-form">
                <input
                  className="deck-modal-input"
                  aria-label="Your name"
                  placeholder="Your Name *"
                  value={fields.name}
                  onChange={(e) => setFields({ ...fields, name: e.target.value })}
                  required
                />
                <input
                  className="deck-modal-input"
                  type="email"
                  aria-label="Email address"
                  placeholder="Email Address *"
                  value={fields.email}
                  onChange={(e) => setFields({ ...fields, email: e.target.value })}
                  required
                />
                <input
                  className="deck-modal-input"
                  type="tel"
                  aria-label="Phone number"
                  placeholder="Phone Number *"
                  value={fields.phone}
                  onChange={(e) => setFields({ ...fields, phone: e.target.value })}
                  required
                />
                <input
                  className="deck-modal-input"
                  aria-label="Organisation (optional)"
                  placeholder="Organisation"
                  value={fields.org}
                  onChange={(e) => setFields({ ...fields, org: e.target.value })}
                />
                {error && (
                  <div className="deck-modal-error" role="alert" aria-live="assertive">
                    {error}
                  </div>
                )}
                <button className="deck-modal-submit" type="button" onClick={submit} disabled={loading}>
                  {loading ? 'Submitting...' : 'Submit request →'}
                </button>
              </div>
              <div className="deck-modal-or">or reach us directly</div>
              <div className="deck-modal-direct">
                <a href="mailto:compliance@kyrocapital.in">✉ compliance@kyrocapital.in</a>
                <a href="tel:+917611111102">✆ 76 111 111 02</a>
              </div>
            </>
          ) : (
            <div className="deck-modal-success" role="status" aria-live="polite">
              ✓ Request received.
              <p style={{ marginTop: '0.5rem' }}>
                We&apos;ll be in touch shortly at <strong>{fields.phone}</strong>.
              </p>
            </div>
          )}
          <div className="deck-modal-disc">
            Kyro India Opportunities Fund – I is a SEBI-registered Category II AIF (Reg. No. IN/AIF2/26-27/2191). For
            eligible investors under SEBI (AIF) Regulations, 2012. For Private Circulation Only.
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GovernanceRequestModal;
